<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { activateAccount, resendActivation } from '../db/auth.js';

const router = useRouter();
const route = useRoute();

const username = ref('');
const email = ref('');
const code = ref(['', '', '', '', '', '']);
const loading = ref(false);
const error = ref('');
const success = ref(false);
const resending = ref(false);

// Refs para los inputs
const inputRefs = ref([]);

// Timer para expiración (24 horas)
const expiresAt = ref(null);
const timeRemaining = ref('');

// Modal state
const showModal = ref(false);
const modalType = ref('error');
const modalTitle = ref('');
const modalMessage = ref('');

const codeString = computed(() => code.value.join(''));

const isCodeComplete = computed(() => {
    return code.value.every(digit => digit !== '');
});

function showModalDialog(type, title, message) {
    modalType.value = type;
    modalTitle.value = title;
    modalMessage.value = message;
    showModal.value = true;
}

function closeModal() {
    showModal.value = false;
}

onMounted(() => {
    username.value = String(route.query.user || '');
    email.value = String(route.query.email || '');

    expiresAt.value = Date.now() + (24 * 60 * 60 * 1000);
    updateTimer();
    setInterval(updateTimer, 1000);

    nextTick(() => {
        if (inputRefs.value[0]) {
            inputRefs.value[0].focus();
        }
    });
});

// Auto-submit cuando el código esté completo
watch(isCodeComplete, async (complete) => {
    if (complete && !loading.value && !success.value) {
        await submit();
    }
});

function updateTimer() {
    if (!expiresAt.value) return;
    const remaining = expiresAt.value - Date.now();
    if (remaining <= 0) {
        timeRemaining.value = 'Código expirado';
        return;
    }

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    timeRemaining.value = `${hours}h ${minutes}m ${seconds}s`;
}

function handleInput(index, event) {
    const value = event.target.value;

    if (!/^\d*$/.test(value)) {
        event.target.value = code.value[index];
        return;
    }

    if (value.length > 1) {
        const digits = value.slice(0, 6).split('');
        digits.forEach((digit, i) => {
            if (i < 6) code.value[i] = digit;
        });
        const lastIndex = Math.min(digits.length - 1, 5);
        if (inputRefs.value[lastIndex]) {
            inputRefs.value[lastIndex].focus();
        }
        return;
    }

    code.value[index] = value;

    if (value && index < 5) {
        nextTick(() => {
            if (inputRefs.value[index + 1]) {
                inputRefs.value[index + 1].focus();
            }
        });
    }
}

function handleKeyDown(index, event) {
    if (event.key === 'Backspace' && !code.value[index] && index > 0) {
        code.value[index - 1] = '';
        nextTick(() => {
            if (inputRefs.value[index - 1]) {
                inputRefs.value[index - 1].focus();
            }
        });
    }

    if (event.key === 'ArrowLeft' && index > 0) {
        inputRefs.value[index - 1]?.focus();
    }
    if (event.key === 'ArrowRight' && index < 5) {
        inputRefs.value[index + 1]?.focus();
    }
}

async function submit() {
    if (!isCodeComplete.value) {
        error.value = 'Por favor completa el código de 6 dígitos';
        return;
    }

    error.value = '';
    loading.value = true;

    try {
        const res = await activateAccount({
            username: username.value,
            code: codeString.value
        });

        loading.value = false;

        if (!res.ok) {
            error.value = res.message || 'Código inválido o expirado';
            code.value = ['', '', '', '', '', ''];
            nextTick(() => {
                if (inputRefs.value[0]) {
                    inputRefs.value[0].focus();
                }
            });
            return;
        }

        success.value = true;
        showModalDialog('success', '¡Cuenta activada!', 'Tu cuenta ha sido activada exitosamente. Ahora puedes iniciar sesión.');

        setTimeout(() => {
            router.push({
                path: '/login',
                query: { user: username.value }
            });
        }, 2000);
    } catch (e) {
        loading.value = false;
        error.value = e?.message || 'Error al activar la cuenta';
        console.error('Error en activación:', e);
    }
}

async function resendCode() {
    if (resending.value) return;

    error.value = '';
    resending.value = true;

    try {
        const res = await resendActivation({ username: username.value });

        resending.value = false;

        if (!res.ok) {
            error.value = res.message || 'No se pudo reenviar el código';
            showModalDialog('error', 'Error', error.value);
            return;
        }

        expiresAt.value = Date.now() + (24 * 60 * 60 * 1000);
        code.value = ['', '', '', '', '', ''];

        showModalDialog('success', 'Código reenviado', 'Se ha enviado un nuevo código a tu correo electrónico.');

        nextTick(() => {
            if (inputRefs.value[0]) {
                inputRefs.value[0].focus();
            }
        });
    } catch (e) {
        resending.value = false;
        error.value = e?.message || 'Error al reenviar código';
        console.error('Error reenviando código:', e);
    }
}

function goBack() {
    router.push('/register');
}
</script>

<template>
    <main class="activation-container">
        <!-- Custom Modal -->
        <Transition name="modal">
            <div v-if="showModal" class="modal-overlay" @click="closeModal">
                <div class="modal-container" @click.stop>
                    <div class="modal-icon" :class="modalType">
                        <span class="material-symbols-rounded">
                            {{ modalType === 'error' ? 'cancel' : 'check_circle' }}
                        </span>
                    </div>
                    <h3 class="modal-title">{{ modalTitle }}</h3>
                    <p class="modal-message">{{ modalMessage }}</p>
                    <button class="modal-btn" @click="closeModal">OK</button>
                </div>
            </div>
        </Transition>

        <div class="activation-wrapper">
            <!-- Left Side - Form -->
            <div class="form-side">
                <div class="form-content">
                    <div class="header">
                        <h1 class="welcome-text">Verifica tu email</h1>
                        <h2 class="brand-name">{{ email }}</h2>
                        <p class="subtitle">Ingresa el código de 6 dígitos enviado a tu correo</p>
                    </div>

                    <div v-if="error" class="error-message">
                        {{ error }}
                    </div>

                    <!-- Code Inputs -->
                    <div class="code-inputs">
                        <input v-for="(digit, index) in code" :key="index" :ref="el => inputRefs[index] = el"
                            v-model="code[index]" type="text" inputmode="numeric" maxlength="1" class="code-digit"
                            :class="{ 'filled': digit, 'error': error }" @input="handleInput(index, $event)"
                            @keydown="handleKeyDown(index, $event)" :disabled="loading || success" />
                    </div>

                    <!-- Timer -->
                    <div class="timer-display">
                        <span class="material-symbols-rounded">schedule</span>
                        <span>Expira en: <strong>{{ timeRemaining }}</strong></span>
                    </div>

                    <!-- Actions -->
                    <button type="button" class="submit-btn" @click="submit"
                        :disabled="!isCodeComplete || loading || success">
                        <span v-if="!loading">ACTIVAR CUENTA</span>
                        <span v-else class="loading-spinner"></span>
                    </button>

                    <button type="button" class="secondary-btn" @click="resendCode"
                        :disabled="resending || loading || success">
                        <span class="material-symbols-rounded">refresh</span>
                        <span v-if="!resending">Reenviar código</span>
                        <span v-else>Reenviando...</span>
                    </button>

                    <p class="footer-text">
                        ¿Problemas con el código?
                        <button type="button" class="link-btn" @click="goBack">Volver al registro</button>
                    </p>
                </div>
            </div>

            <!-- Right Side - Image -->
            <div class="illustration-side">
                <div class="image-container">
                    <img src="/img/sushi_hero.png" alt="Sushito" class="activation-image" />
                    <div class="image-overlay"></div>
                </div>
            </div>
        </div>
    </main>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

.activation-container {
    display: flex;
    min-height: 100vh;
    align-items: center;
    justify-content: center;
    font-family: 'Poppins', sans-serif;
    background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
    padding: 2rem;
}

.activation-wrapper {
    max-width: 900px;
    width: 100%;
    display: flex;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    border-radius: 16px;
    overflow: hidden;
    background: var(--panel-bg);
    border: 1px solid var(--border);
}

/* ===== LEFT SIDE - FORM ===== */
.form-side {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    background: transparent;
}

.form-content {
    width: 100%;
    max-width: 380px;
}

.header {
    margin-bottom: 1.5rem;
    text-align: center;
}

.welcome-text {
    font-size: 1rem;
    font-weight: 300;
    color: var(--muted);
    margin-bottom: 0.15rem;
}

.brand-name {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--text);
    margin: 0 0 0.5rem 0;
    word-break: break-all;
}

.subtitle {
    font-size: 0.8rem;
    color: var(--muted);
    line-height: 1.4;
}

.error-message {
    padding: 0.5rem;
    background: rgba(255, 107, 107, 0.1);
    border-left: 3px solid #ff6b6b;
    color: #ff6b6b;
    font-size: 0.75rem;
    border-radius: 4px;
    margin-bottom: 1rem;
}

/* Code Inputs */
.code-inputs {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    margin-bottom: 1rem;
}

.code-digit {
    width: 3rem;
    height: 3.5rem;
    font-size: 1.5rem;
    font-weight: 700;
    text-align: center;
    border: none;
    border-bottom: 2px solid rgba(255, 255, 255, 0.3);
    background: transparent;
    color: #ffffff;
    transition: all 0.3s;
    outline: none;
    font-family: 'Poppins', sans-serif;
}

.code-digit:focus {
    border-bottom-color: #C6923D;
    transform: scale(1.05);
}

.code-digit.filled {
    border-bottom-color: #DFC05E;
    color: #DFC05E;
}

.code-digit.error {
    border-bottom-color: #ff6b6b;
    animation: shake 0.5s;
}

@keyframes shake {

    0%,
    100% {
        transform: translateX(0);
    }

    25% {
        transform: translateX(-5px);
    }

    75% {
        transform: translateX(5px);
    }
}

.code-digit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Timer */
.timer-display {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background: rgba(198, 146, 61, 0.1);
    border-radius: 8px;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 1rem;
    border: 1px solid rgba(198, 146, 61, 0.2);
}

.timer-display .material-symbols-rounded {
    color: #C6923D;
    font-size: 1.1rem;
}

.timer-display strong {
    color: #C6923D;
    font-weight: 600;
}

/* Buttons */
.submit-btn {
    width: 100%;
    padding: 0.65rem;
    background: linear-gradient(135deg, #C6923D, #DFC05E);
    color: #000000;
    border: none;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.3s;
    margin-bottom: 0.75rem;
}

.submit-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(198, 146, 61, 0.4);
}

.submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.secondary-btn {
    width: 100%;
    padding: 0.6rem;
    background: transparent;
    color: #C6923D;
    border: 1.5px solid #C6923D;
    border-radius: 16px;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
}

.secondary-btn:hover:not(:disabled) {
    background: rgba(198, 146, 61, 0.15);
}

.secondary-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.secondary-btn .material-symbols-rounded {
    font-size: 1.1rem;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.loading-spinner {
    display: inline-block;
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

/* Footer */
.footer-text {
    text-align: center;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.7);
}

.link-btn {
    background: none;
    border: none;
    color: #C6923D;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    text-decoration: none;
    transition: color 0.3s;
}

.link-btn:hover {
    color: #DFC05E;
    text-decoration: underline;
}

/* ===== RIGHT SIDE - IMAGE ===== */
.illustration-side {
    flex: 1;
    position: relative;
    overflow: hidden;
    background: #000;
}

.image-container {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 500px;
}

.activation-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transition: transform 0.6s ease;
}

.activation-wrapper:hover .activation-image {
    transform: scale(1.05);
}

.image-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(198, 146, 61, 0.2) 0%, rgba(223, 192, 94, 0.25) 100%);
    pointer-events: none;
}

/* Responsive */
@media (max-width: 968px) {
    .activation-wrapper {
        flex-direction: column;
    }

    .illustration-side {
        min-height: 300px;
        order: -1;
    }

    .form-side {
        padding: 2rem 1rem;
    }
}

@media (max-width: 480px) {
    .activation-container {
        padding: 1rem;
    }

    .activation-wrapper {
        border-radius: 12px;
    }

    .code-digit {
        width: 2.5rem;
        height: 3rem;
        font-size: 1.25rem;
    }

    .form-content {
        max-width: 100%;
    }
}

/* ===== CUSTOM MODAL ===== */
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 1rem;
}

.modal-container {
    background: rgba(20, 24, 30, 0.98);
    border-radius: 20px;
    padding: 2rem;
    max-width: 400px;
    width: 100%;
    border: 1px solid rgba(198, 146, 61, 0.3);
    color: #ffffff;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    animation: modalSlideIn 0.3s ease-out;
}

@media (prefers-color-scheme: dark) {
    .modal-container {
        background: #2a2a3a;
    }
}

@keyframes modalSlideIn {
    from {
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.modal-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: iconPop 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes iconPop {
    0% {
        transform: scale(0);
        opacity: 0;
    }

    50% {
        transform: scale(1.1);
    }

    100% {
        transform: scale(1);
        opacity: 1;
    }
}

.modal-icon.error {
    background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
}

.modal-icon.success {
    background: linear-gradient(135deg, #51cf66, #00C2A8);
}

.modal-icon .material-symbols-rounded {
    font-size: 3rem;
    color: white;
}

.modal-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #ffffff;
    margin: 0;
    text-align: center;
}

.modal-message {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.8);
    text-align: center;
    line-height: 1.5;
    margin: 0;
}

.modal-btn {
    width: 100%;
    padding: 0.75rem;
    background: linear-gradient(135deg, #C6923D, #DFC05E);
    color: #000000;
    border: none;
    border-radius: 12px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    margin-top: 0.5rem;
}

.modal-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(198, 146, 61, 0.4);
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
    transition: transform 0.3s ease;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
    transform: scale(0.9) translateY(-20px);
}

/* Overrides explícitos cuando data-theme=dark está activo */
:global([data-theme="dark"]) .activation-container {
    background: #0b0f14;
}

:global([data-theme="dark"]) .activation-wrapper {
    background: rgba(20, 24, 30, 0.96);
    border: 1px solid rgba(255, 255, 255, 0.18);
}

:global([data-theme="dark"]) .form-side {
    background: rgba(20, 24, 30, 0.96);
}

:global([data-theme="dark"]) .form-content {
    color: #f1f5f9;
}

:global([data-theme="dark"]) .welcome-text {
    color: #cbd5e1;
}

:global([data-theme="dark"]) .brand-name {
    color: #f1f5f9;
}

:global([data-theme="dark"]) .subtitle {
    color: #cbd5e1;
}

:global([data-theme="dark"]) .input-field {
    color: #f1f5f9;
    border-bottom-color: rgba(255, 255, 255, 0.22);
}

:global([data-theme="dark"]) .input-field::placeholder {
    color: #cbd5e1;
}

:global([data-theme="dark"]) .error-message {
    color: #ff6b6b;
    background: rgba(255, 107, 107, 0.1);
}
</style>
